import { ReactNode, useState } from 'react';

import { LoginPage } from './LoginPage';

import { Button } from '@frontend/components/ui/button';
import { useCurrentUser } from '@frontend/features/auth/hooks/useCurrentUser';
import { useIsAuthenticatedLocal } from '@frontend/features/auth/hooks/useIsAuthenticatedLocal';
import { useUserDirectory } from '@frontend/features/auth/hooks/useUserDirectory';
import { logout } from '@frontend/features/auth/util/util.auth.logout';

interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const isAuthenticated = useIsAuthenticatedLocal();
  const [isRetrying, setIsRetrying] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Check if current path is the auth callback route
  const isCallbackRoute = window.location.pathname === '/auth/callback';

  // Allow callback route to render without authentication
  if (isCallbackRoute) {
    return <>{children}</>;
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Only fetch user data after authentication is confirmed
  return (
    <AuthenticatedGuard
      isRetrying={isRetrying}
      setIsRetrying={setIsRetrying}
      isLoggingOut={isLoggingOut}
      setIsLoggingOut={setIsLoggingOut}
    >
      {children}
    </AuthenticatedGuard>
  );
}

// Separate component that only renders when authenticated
function AuthenticatedGuard({
  children,
  isRetrying,
  setIsRetrying,
  isLoggingOut,
  setIsLoggingOut,
}: {
  children: ReactNode;
  isRetrying: boolean;
  setIsRetrying: (value: boolean) => void;
  isLoggingOut: boolean;
  setIsLoggingOut: (value: boolean) => void;
}) {
  const { user, isLoading, refetch } = useCurrentUser();

  // Prefetch user directory for entire app
  useUserDirectory();

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await refetch();
    } finally {
      setIsRetrying(false);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
      setIsLoggingOut(false);
    }
  };

  // Show loading while checking authentication and role
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // Authenticated but no role assigned → show access denied
  if (user && user.role === null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center max-w-md px-4">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Access Denied
          </h1>
          <p className="text-gray-600 mb-2">
            You don&apos;t have permission to access this application.
          </p>
          <p className="text-gray-600 mb-6">
            Please contact your administrator to request access and have a role
            assigned (Admin, PNOps, or ViewOnly).
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              onClick={handleRetry}
              disabled={isRetrying || isLoggingOut}
              variant="default"
            >
              {isRetrying ? 'Checking...' : 'Check Again'}
            </Button>
            <Button
              onClick={handleLogout}
              disabled={isRetrying || isLoggingOut}
              variant="outline"
            >
              {isLoggingOut ? 'Logging out...' : 'Log Out'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated and has role → allow access
  return <>{children}</>;
}
